<template>
  <div class="doc-upload">
    <!-- Selector de período (para agentes financieros o cuando se requiere) -->
    <div v-if="requiresPeriod || isFinancialAgent" class="period-selector">
      <div class="period-header">
        <i class="pi pi-calendar" />
        <span>Período contable <span class="required">*</span></span>
      </div>

      <!-- Selector de tipo de período -->
      <div class="period-type-selector">
        <Dropdown
          v-model="periodType"
          :options="periodTypeOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Selecciona el tipo de período"
          class="w-full"
          @change="onPeriodTypeChange"
        />
      </div>

      <!-- Selector de año -->
      <div v-if="periodType" class="field">
        <label>Año</label>
        <Dropdown
          v-model="selectedYear"
          :options="yearOptions"
          placeholder="Selecciona el año"
          class="w-full"
          @change="onYearChange"
        />
      </div>

      <!-- Selector específico según el tipo -->
      <div v-if="periodType === 'month' && selectedYear" class="field">
        <label>Mes</label>
        <Dropdown
          v-model="selectedMonth"
          :options="monthOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Selecciona el mes"
          class="w-full"
          @change="onMonthChange"
        />
      </div>

      <div v-if="periodType === 'quarter' && selectedYear" class="field">
        <label>Trimestre</label>
        <Dropdown
          v-model="selectedQuarter"
          :options="quarterOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Selecciona el trimestre"
          class="w-full"
          @change="onQuarterChange"
        />
      </div>

      <div v-if="periodType === 'semester' && selectedYear" class="field">
        <label>Semestre</label>
        <Dropdown
          v-model="selectedSemester"
          :options="semesterOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Selecciona el semestre"
          class="w-full"
          @change="onSemesterChange"
        />
      </div>

      <!-- Período personalizado -->
      <div v-if="periodType === 'custom'" class="period-dates">
        <div class="field">
          <label>Desde</label>
          <Calendar
            v-model="periodStart"
            dateFormat="dd/mm/yy"
            :showIcon="true"
            placeholder="01/01/2025"
            class="w-full"
          />
        </div>
        <div class="field">
          <label>Hasta</label>
          <Calendar
            v-model="periodEnd"
            dateFormat="dd/mm/yy"
            :showIcon="true"
            placeholder="31/12/2025"
            :minDate="periodStart"
            class="w-full"
          />
        </div>
      </div>

      <div v-if="periodLabel" class="period-label-preview">
        <i class="pi pi-tag" />
        <span>{{ periodLabel }}</span>
      </div>

      <Message v-if="periodError" severity="warn" :closable="false" class="mt-1">
        {{ periodError }}
      </Message>
    </div>

    <!-- Selector de marco normativo (solo para agentes financieros) -->
    <div v-if="isFinancialAgent" class="framework-selector">
      <div class="framework-header">
        <i class="pi pi-book" />
        <span>Marco normativo <span class="required">*</span></span>
      </div>
      <Dropdown
        v-model="selectedFramework"
        :options="frameworkOptions"
        optionLabel="label"
        optionValue="value"
        :loading="loadingCatalogs"
        placeholder="Selecciona el marco normativo"
        class="w-full"
      />
      <Message v-if="frameworkError" severity="warn" :closable="false" class="mt-1">
        {{ frameworkError }}
      </Message>
    </div>

    <!-- Selector de tipo de entidad (solo para agentes financieros) -->
    <div v-if="isFinancialAgent" class="entity-type-selector">
      <div class="entity-type-header">
        <i class="pi pi-building" />
        <span>Tipo de entidad <span class="required">*</span></span>
      </div>
      <Dropdown
        v-model="selectedEntityType"
        :options="entityTypeOptions"
        optionLabel="label"
        optionValue="value"
        :loading="loadingCatalogs"
        :disabled="loadingCatalogs || !selectedFramework"
        placeholder="Selecciona el tipo de entidad"
        class="w-full"
      />
      <Message v-if="entityTypeError" severity="warn" :closable="false" class="mt-1">
        {{ entityTypeError }}
      </Message>
    </div>

    <div v-if="isAdmin" class="ownership-selector">
      <div class="ownership-header">
        <i class="pi pi-sitemap" />
        <span>Propietario del documento</span>
      </div>
      <div class="field">
        <label>Tenant</label>
        <Dropdown
          v-model="ownershipTenantId"
          :options="ownershipTenantOptions"
          optionLabel="label"
          optionValue="value"
          :loading="loadingOwnership"
          placeholder="Selecciona tenant"
          class="w-full"
        />
      </div>
      <div class="field">
        <label>Subtenant (opcional)</label>
        <Dropdown
          :modelValue="ownershipSubTenantId || ''"
          :options="ownershipSubTenantOptions"
          optionLabel="label"
          optionValue="value"
          :loading="loadingOwnership"
          :disabled="!ownershipTenantId"
          placeholder="Sin subtenant"
          class="w-full"
          @update:modelValue="(value) => { ownershipSubTenantId = value || null; }"
        />
      </div>
    </div>

    <!-- Zona de drop -->
    <div
      :class="['drop-zone', { 'drag-over': isDragging, 'has-file': pendingFile }]"
      @dragover.prevent="isDragging = true"
      @dragleave="isDragging = false"
      @drop.prevent="onDrop"
      @click="$refs.fileInput.click()"
    >
      <input
        ref="fileInput"
        type="file"
        :accept="acceptAttr"
        style="display:none"
        @change="onFileSelected"
      />

      <div v-if="!pendingFile" class="drop-content">
        <i class="pi pi-cloud-upload drop-icon" />
        <p class="drop-title">Arrastra un archivo o haz clic para seleccionar</p>
        <p class="drop-hint">{{ acceptLabel }}</p>
        <p class="drop-hint">Máximo {{ maxSizeMB }} MB</p>
      </div>

      <div v-else class="file-preview">
        <i :class="['pi', fileIcon(pendingFile.name), 'file-icon']" />
        <div class="file-info">
          <span class="file-name">{{ pendingFile.name }}</span>
          <span class="file-size">{{ formatSize(pendingFile.size) }}</span>
        </div>
        <Button icon="pi pi-times" text rounded severity="secondary" size="small" @click.stop="clearFile" />
      </div>
    </div>

    <!-- Descripción opcional -->
    <div v-if="showDescription && pendingFile" class="field mt-2">
      <InputText v-model="description" placeholder="Descripción del documento (opcional)" class="w-full" />
    </div>

    <!-- Barra de progreso -->
    <div v-if="uploading" class="progress-wrap mt-2">
      <ProgressBar :value="progress" />
      <span class="progress-label">{{ progress }}% — {{ progressLabel }}</span>
    </div>

    <!-- Error -->
    <Message v-if="error" severity="error" :closable="true" @close="error = ''" class="mt-2">
      {{ error }}
    </Message>

    <!-- Botón de subida -->
    <Button
      v-if="pendingFile && !uploading"
      :label="uploadLabel"
      :icon="uploadIcon"
      class="w-full mt-2"
      :loading="uploading"
      :disabled="((requiresPeriod || isFinancialAgent) && !periodStart) || (isFinancialAgent && (!selectedFramework || !selectedEntityType)) || (isAdmin && !ownershipTenantId)"
      @click="upload"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import ProgressBar from 'primevue/progressbar';
import Message from 'primevue/message';
import Calendar from 'primevue/calendar';
import Dropdown from 'primevue/dropdown';
import { documentsService, financialService } from '../../services/documents.js';
import { tenantsService } from '../../services/tenants.js';
import { useAuthStore } from '../../stores/auth.js';

const props = defineProps({
  agentId: { type: Number, default: null },
  agentCategory: { type: String, default: 'general' },
  accept: { type: Array, default: () => ['pdf', 'xlsx', 'xls', 'csv', 'docx', 'txt'] },
  maxSizeMB: { type: Number, default: 20 },
  showDescription: { type: Boolean, default: true },
  /** Forzar selector de período aunque el agente no sea financiero */
  requiresPeriod: { type: Boolean, default: false },
  uploadLabel: { type: String, default: 'Subir documento' },
  uploadIcon: { type: String, default: 'pi pi-upload' },
  analysisTimeoutMs: { type: Number, default: 900000 },
  useAsyncFinancialAnalysis: { type: Boolean, default: false },
  taskPollIntervalMs: { type: Number, default: 2500 },
});

const emit = defineEmits([
  'uploaded',
  'analyzed',
  'error',
  'task-queued',
  'task-progress',
  'task-completed',
  'task-failed',
]);
const authStore = useAuthStore();
const isAdmin = computed(() => authStore.isAdmin);

// Archivo
const pendingFile = ref(null);
const description = ref('');
const uploading = ref(false);
const progress = ref(0);
const progressLabel = ref('Subiendo...');
const error = ref('');
const isDragging = ref(false);

// Período
const periodStart = ref(null);
const periodEnd = ref(null);
const periodError = ref('');
const periodType = ref(null);
const selectedYear = ref(null);
const selectedMonth = ref(null);
const selectedQuarter = ref(null);
const selectedSemester = ref(null);

// Opciones de tipo de período
const periodTypeOptions = [
  { label: 'Mensual', value: 'month' },
  { label: 'Trimestral', value: 'quarter' },
  { label: 'Semestral', value: 'semester' },
  { label: 'Anual', value: 'year' },
  { label: 'Personalizado', value: 'custom' },
];

// Opciones de año (últimos 20 años + año actual + próximo año)
const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear + 1; i >= currentYear - 20; i--) {
    years.push(i);
  }
  return years;
});

// Opciones de mes
const monthOptions = [
  { label: 'Enero', value: 0 },
  { label: 'Febrero', value: 1 },
  { label: 'Marzo', value: 2 },
  { label: 'Abril', value: 3 },
  { label: 'Mayo', value: 4 },
  { label: 'Junio', value: 5 },
  { label: 'Julio', value: 6 },
  { label: 'Agosto', value: 7 },
  { label: 'Septiembre', value: 8 },
  { label: 'Octubre', value: 9 },
  { label: 'Noviembre', value: 10 },
  { label: 'Diciembre', value: 11 },
];

// Opciones de trimestre
const quarterOptions = [
  { label: 'Q1 (Ene-Mar)', value: 1 },
  { label: 'Q2 (Abr-Jun)', value: 2 },
  { label: 'Q3 (Jul-Sep)', value: 3 },
  { label: 'Q4 (Oct-Dic)', value: 4 },
];

// Opciones de semestre
const semesterOptions = [
  { label: 'S1 (Ene-Jun)', value: 1 },
  { label: 'S2 (Jul-Dic)', value: 2 },
];

// Función auxiliar para obtener el último día del mes
const lastDayOfMonth = (year, month) => new Date(year, month + 1, 0).getDate();

// Manejadores de cambio
const onPeriodTypeChange = () => {
  selectedMonth.value = null;
  selectedQuarter.value = null;
  selectedSemester.value = null;
  periodStart.value = null;
  periodEnd.value = null;
  periodError.value = '';
  
  // Si es anual y ya hay año seleccionado, aplicar automáticamente
  if (periodType.value === 'year' && selectedYear.value) {
    onYearChange();
  }
};

const onYearChange = () => {
  if (!selectedYear.value) return;
  
  if (periodType.value === 'year') {
    // Año completo
    periodStart.value = new Date(selectedYear.value, 0, 1);
    periodEnd.value = new Date(selectedYear.value, 11, 31);
  } else {
    // Resetear selecciones específicas
    selectedMonth.value = null;
    selectedQuarter.value = null;
    selectedSemester.value = null;
    periodStart.value = null;
    periodEnd.value = null;
  }
  periodError.value = '';
};

const onMonthChange = () => {
  if (!selectedYear.value || selectedMonth.value === null) return;
  
  periodStart.value = new Date(selectedYear.value, selectedMonth.value, 1);
  periodEnd.value = new Date(selectedYear.value, selectedMonth.value, lastDayOfMonth(selectedYear.value, selectedMonth.value));
  periodError.value = '';
};

const onQuarterChange = () => {
  if (!selectedYear.value || !selectedQuarter.value) return;
  
  const quarterStartMonth = (selectedQuarter.value - 1) * 3;
  const quarterEndMonth = quarterStartMonth + 2;
  
  periodStart.value = new Date(selectedYear.value, quarterStartMonth, 1);
  periodEnd.value = new Date(selectedYear.value, quarterEndMonth, lastDayOfMonth(selectedYear.value, quarterEndMonth));
  periodError.value = '';
};

const onSemesterChange = () => {
  if (!selectedYear.value || !selectedSemester.value) return;
  
  if (selectedSemester.value === 1) {
    periodStart.value = new Date(selectedYear.value, 0, 1);
    periodEnd.value = new Date(selectedYear.value, 5, 30);
  } else {
    periodStart.value = new Date(selectedYear.value, 6, 1);
    periodEnd.value = new Date(selectedYear.value, 11, 31);
  }
  periodError.value = '';
};

// Marco normativo (framework)
const selectedFramework = ref(null);
const frameworkError = ref('');
const loadingCatalogs = ref(false);

const frameworkOptions = ref([]);

// Tipo de entidad
const selectedEntityType = ref(null);
const entityTypeError = ref('');

const entityTypeOptions = ref([]);

// Ownership (admin)
const ownershipTenantId = ref(null);
const ownershipSubTenantId = ref(null);
const ownershipTenantOptions = ref([]);
const ownershipSubTenantOptions = ref([{ label: 'Sin subtenant', value: '' }]);
const loadingOwnership = ref(false);

const applyFinancialDefaultsFromScope = async ({ tenantId, subTenantId, force }) => {
  if (!isFinancialAgent.value || !tenantId) return;
  try {
    const tenant = await tenantsService.getTenantById(tenantId);
    let preferredCompanyType = tenant?.default_company_type_code || null;
    let preferredFramework = tenant?.default_accounting_framework_code || null;

    if (subTenantId) {
      const subTenants = await tenantsService.getSubTenants(tenantId);
      const selectedSubTenant = Array.isArray(subTenants)
        ? subTenants.find((item) => item.id === subTenantId)
        : null;
      if (selectedSubTenant?.default_company_type_code) {
        preferredCompanyType = selectedSubTenant.default_company_type_code;
      }
      if (selectedSubTenant?.default_accounting_framework_code) {
        preferredFramework = selectedSubTenant.default_accounting_framework_code;
      }
    }

    if (preferredFramework && (force || !selectedFramework.value)) {
      selectedFramework.value = preferredFramework;
      await loadCompanyTypesByFramework(preferredFramework);
    }
    if (preferredCompanyType && (force || !selectedEntityType.value)) {
      const available = entityTypeOptions.value.some((item) => item.value === preferredCompanyType);
      if (available) selectedEntityType.value = preferredCompanyType;
    }
  } catch (e) {
    error.value = e.response?.data?.detail || e.message || 'No fue posible cargar defaults financieros del tenant/subtenant.';
  }
};

const loadFrameworks = async () => {
  loadingCatalogs.value = true;
  try {
    const frameworks = await financialService.listAccountingFrameworks();
    frameworkOptions.value = frameworks.map((item) => ({
      label: item.name,
      value: item.code,
    }));
    if (!selectedFramework.value && frameworkOptions.value.length > 0) {
      selectedFramework.value = frameworkOptions.value[0].value;
    }
  } catch (e) {
    const msg = e.response?.data?.detail || e.message || 'No fue posible cargar los marcos normativos.';
    error.value = msg;
  } finally {
    loadingCatalogs.value = false;
  }
};

const loadCompanyTypesByFramework = async (frameworkCode) => {
  if (!frameworkCode) {
    entityTypeOptions.value = [];
    selectedEntityType.value = null;
    return;
  }
  loadingCatalogs.value = true;
  try {
    const companyTypes = await financialService.listCompanyTypesByAccountingFramework(frameworkCode);
    entityTypeOptions.value = companyTypes.map((item) => ({
      label: item.name,
      value: item.code,
    }));
    const currentIsValid = entityTypeOptions.value.some((item) => item.value === selectedEntityType.value);
    if (!currentIsValid) {
      selectedEntityType.value = entityTypeOptions.value.length > 0
        ? entityTypeOptions.value[0].value
        : null;
    }
  } catch (e) {
    entityTypeOptions.value = [];
    selectedEntityType.value = null;
    const msg = e.response?.data?.detail || e.message || 'No fue posible cargar los tipos de entidad.';
    error.value = msg;
  } finally {
    loadingCatalogs.value = false;
  }
};

const isFinancialAgent = computed(() =>
  ['financiero', 'financial', 'contable', 'contabilidad'].includes(
    props.agentCategory?.toLowerCase()
  )
);

watch(
  selectedFramework,
  async (frameworkCode) => {
    if (!isFinancialAgent.value) return;
    await loadCompanyTypesByFramework(frameworkCode);
  }
);

onMounted(async () => {
  if (!isFinancialAgent.value) return;
  await loadFrameworks();
  if (isAdmin.value && ownershipTenantId.value) {
    await applyFinancialDefaultsFromScope({
      tenantId: ownershipTenantId.value,
      subTenantId: ownershipSubTenantId.value,
      force: false,
    });
  } else if (authStore.user?.tenant_id) {
    await applyFinancialDefaultsFromScope({
      tenantId: authStore.user.tenant_id,
      subTenantId: authStore.user?.sub_tenant_id || null,
      force: false,
    });
  }
  if (selectedFramework.value) await loadCompanyTypesByFramework(selectedFramework.value);
});

const loadOwnershipTenants = async () => {
  if (!isAdmin.value) return;
  loadingOwnership.value = true;
  try {
    const tenants = await tenantsService.getTenants();
    ownershipTenantOptions.value = Array.isArray(tenants)
      ? tenants.map((item) => ({
          label: `${item.name} (${item.id})`,
          value: item.id,
        }))
      : [];
    if (!ownershipTenantId.value && ownershipTenantOptions.value.length > 0) {
      ownershipTenantId.value = ownershipTenantOptions.value[0].value;
    }
  } catch (e) {
    error.value = e.response?.data?.detail || e.message || 'No fue posible cargar tenants.';
  } finally {
    loadingOwnership.value = false;
  }
};

const loadOwnershipSubTenants = async (tenantId) => {
  if (!isAdmin.value || !tenantId) {
    ownershipSubTenantOptions.value = [{ label: 'Sin subtenant', value: '' }];
    ownershipSubTenantId.value = null;
    return;
  }
  loadingOwnership.value = true;
  try {
    const subTenants = await tenantsService.getSubTenants(tenantId);
    const mapped = Array.isArray(subTenants)
      ? subTenants.map((item) => ({
          label: `${item.name} (${item.id})`,
          value: item.id,
        }))
      : [];
    ownershipSubTenantOptions.value = [{ label: 'Sin subtenant', value: '' }, ...mapped];
    const stillValid = ownershipSubTenantOptions.value.some((item) => item.value === (ownershipSubTenantId.value || ''));
    if (!stillValid) ownershipSubTenantId.value = null;
  } catch (e) {
    ownershipSubTenantOptions.value = [{ label: 'Sin subtenant', value: '' }];
    ownershipSubTenantId.value = null;
    error.value = e.response?.data?.detail || e.message || 'No fue posible cargar subtenants.';
  } finally {
    loadingOwnership.value = false;
  }
};

watch(
  isAdmin,
  async (admin) => {
    if (!admin) return;
    await loadOwnershipTenants();
    if (ownershipTenantId.value) {
      await loadOwnershipSubTenants(ownershipTenantId.value);
    }
  },
  { immediate: true }
);

watch(
  ownershipTenantId,
  async (tenantId) => {
    if (!isAdmin.value) return;
    await loadOwnershipSubTenants(tenantId);
    await applyFinancialDefaultsFromScope({
      tenantId,
      subTenantId: ownershipSubTenantId.value,
      force: true,
    });
  }
);

watch(
  ownershipSubTenantId,
  async (subTenantId) => {
    if (!isAdmin.value || !ownershipTenantId.value) return;
    await applyFinancialDefaultsFromScope({
      tenantId: ownershipTenantId.value,
      subTenantId,
      force: true,
    });
  }
);

// Etiqueta automática del período
const periodLabel = computed(() => {
  if (!periodStart.value || !periodEnd.value) return '';
  
  const start = periodStart.value;
  const end = periodEnd.value;
  const year = end.getFullYear();
  const months = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
                  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  
  // Detectar tipo de período basado en las fechas
  const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
  
  if (days >= 360) return `Año ${year}`;
  if (days >= 175) return `Semestre ${start.getMonth() < 6 ? 1 : 2} - ${year}`;
  if (days >= 85) return `Q${Math.floor(start.getMonth() / 3) + 1} ${year}`;
  if (days >= 28 && days <= 31) return `${months[start.getMonth()]} ${year}`;
  
  // Período personalizado
  return `${start.getDate()}/${start.getMonth() + 1}/${year} - ${end.getDate()}/${end.getMonth() + 1}/${end.getFullYear()}`;
});

// Períodos rápidos
const quickPeriods = computed(() => {
  const now = new Date();
  const y = now.getFullYear();
  const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  
  // Función auxiliar para obtener el último día del mes
  const lastDayOfMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  
  // Períodos anuales
  const yearPeriods = [
    { label: `Año ${y}`,     start: new Date(y, 0, 1),  end: new Date(y, 11, 31) },
    { label: `Año ${y - 1}`, start: new Date(y-1, 0, 1), end: new Date(y-1, 11, 31) },
  ];
  
  // Períodos semestrales
  const semesterPeriods = [
    { label: `S1 ${y}`,      start: new Date(y, 0, 1),  end: new Date(y, 5, 30) },
    { label: `S2 ${y}`,      start: new Date(y, 6, 1),  end: new Date(y, 11, 31) },
  ];
  
  // Períodos trimestrales
  const quarterPeriods = [
    { label: `Q1 ${y}`,      start: new Date(y, 0, 1),  end: new Date(y, 2, 31) },
    { label: `Q2 ${y}`,      start: new Date(y, 3, 1),  end: new Date(y, 5, 30) },
    { label: `Q3 ${y}`,      start: new Date(y, 6, 1),  end: new Date(y, 8, 30) },
    { label: `Q4 ${y}`,      start: new Date(y, 9, 1),  end: new Date(y, 11, 31) },
  ];
  
  // Períodos mensuales del año actual
  const monthPeriods = months.map((month, idx) => ({
    label: `${month} ${y}`,
    start: new Date(y, idx, 1),
    end: new Date(y, idx, lastDayOfMonth(y, idx))
  }));
  
  return [...yearPeriods, ...semesterPeriods, ...quarterPeriods, ...monthPeriods];
});

const applyQuickPeriod = (opt) => {
  periodStart.value = opt.start;
  periodEnd.value = opt.end;
  selectedQuick.value = opt.label;
  periodError.value = '';
};

// Formato de fecha para el backend (YYYY-MM-DD)
const toISODate = (d) => {
  if (!d) return null;
  const date = d instanceof Date ? d : new Date(d);
  return date.toISOString().split('T')[0];
};

// Archivo
const acceptAttr = computed(() => props.accept.map(e => `.${e}`).join(','));
const acceptLabel = computed(() => {
  const map = { pdf: 'PDF', xlsx: 'Excel', xls: 'Excel', csv: 'CSV', docx: 'Word', txt: 'Texto', png: 'PNG', jpg: 'JPG' };
  return props.accept.map(e => map[e] || e.toUpperCase()).join(', ');
});

const fileIcon = (name) => {
  const ext = name.split('.').pop().toLowerCase();
  return { pdf: 'pi-file-pdf', xlsx: 'pi-file-excel', xls: 'pi-file-excel', csv: 'pi-table', docx: 'pi-file-word', txt: 'pi-file', png: 'pi-image', jpg: 'pi-image' }[ext] || 'pi-file';
};

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const validateFile = (file) => {
  const ext = file.name.split('.').pop().toLowerCase();
  if (!props.accept.includes(ext)) return `Formato .${ext} no permitido. Usa: ${acceptLabel.value}`;
  if (file.size > props.maxSizeMB * 1024 * 1024) return `El archivo supera el límite de ${props.maxSizeMB} MB`;
  return null;
};

const onFileSelected = (e) => {
  const file = e.target.files[0];
  if (!file) return;
  const err = validateFile(file);
  if (err) { error.value = err; return; }
  pendingFile.value = file;
  error.value = '';
};

const onDrop = (e) => {
  isDragging.value = false;
  const file = e.dataTransfer.files[0];
  if (!file) return;
  const err = validateFile(file);
  if (err) { error.value = err; return; }
  pendingFile.value = file;
  error.value = '';
};

const clearFile = () => {
  pendingFile.value = null;
  description.value = '';
  progress.value = 0;
  error.value = '';
  selectedFramework.value = null;
  frameworkError.value = '';
  selectedEntityType.value = null;
  entityTypeError.value = '';
  ownershipSubTenantId.value = null;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const pollFinancialTask = async (taskId, tenantId) => {
  const startedAt = Date.now();
  const timeoutMs = props.analysisTimeoutMs || 900000;

  while ((Date.now() - startedAt) < timeoutMs) {
    const task = await financialService.getAnalysisTask(taskId, { tenantId });
    emit('task-progress', {
      taskId,
      status: task?.status,
      progressPercent: Number(task?.progress_percent ?? 0),
      message: task?.message || null,
      errorMessage: task?.error_message || null,
      result: task?.result || null,
      filename: task?.filename || null,
      analysisRunId: task?.analysis_run_id || null,
    });
    const taskProgress = Number(task?.progress_percent ?? 0);
    if (Number.isFinite(taskProgress)) {
      // Etapa job: 40% -> 100%
      const mappedProgress = 40 + Math.round(Math.max(0, Math.min(100, taskProgress)) * 0.6);
      progress.value = Math.max(progress.value, Math.max(40, Math.min(100, mappedProgress)));
    }
    progressLabel.value = task?.message || 'Procesando análisis...';

    if (task?.status === 'completed') {
      progress.value = 100;
      if (!task.result) {
        throw new Error('La tarea terminó sin resultado de análisis.');
      }
      emit('task-completed', {
        taskId,
        status: task?.status,
        progressPercent: Number(task?.progress_percent ?? 100),
        message: task?.message || null,
        errorMessage: task?.error_message || null,
        result: task?.result || null,
        filename: task?.filename || null,
        analysisRunId: task?.analysis_run_id || null,
      });
      return task.result;
    }
    if (task?.status === 'failed') {
      emit('task-failed', {
        taskId,
        status: task?.status,
        progressPercent: Number(task?.progress_percent ?? 100),
        message: task?.message || null,
        errorMessage: task?.error_message || null,
        result: task?.result || null,
        filename: task?.filename || null,
        analysisRunId: task?.analysis_run_id || null,
      });
      throw new Error(task?.error_message || task?.message || 'El análisis falló en background.');
    }
    await sleep(Math.max(800, props.taskPollIntervalMs || 2500));
  }
  emit('task-failed', {
    taskId,
    status: 'failed',
    progressPercent: 100,
    message: 'Timeout del seguimiento de tarea',
    errorMessage: 'El análisis está tardando más de lo esperado.',
    result: null,
    filename: null,
    analysisRunId: null,
  });
  throw new Error('El análisis está tardando más de lo esperado.');
};

const upload = async () => {
  if (!pendingFile.value) return;

  // Validar período si es requerido
  if (isAdmin.value && !ownershipTenantId.value) {
    error.value = 'Selecciona el tenant propietario antes de subir el documento.';
    return;
  }
  if ((isFinancialAgent.value || props.requiresPeriod) && !periodStart.value) {
    periodError.value = 'Selecciona el período contable antes de subir el documento.';
    return;
  }
  if (periodStart.value && periodEnd.value && periodEnd.value < periodStart.value) {
    periodError.value = 'La fecha de fin debe ser posterior a la fecha de inicio.';
    return;
  }
  periodError.value = '';

  // Validar marco normativo si es agente financiero
  if (isFinancialAgent.value && !selectedFramework.value) {
    frameworkError.value = 'Selecciona el marco normativo antes de subir el documento.';
    return;
  }
  frameworkError.value = '';

  // Validar tipo de entidad si es agente financiero
  if (isFinancialAgent.value && !selectedEntityType.value) {
    entityTypeError.value = 'Selecciona el tipo de entidad antes de subir el documento.';
    return;
  }
  entityTypeError.value = '';

  uploading.value = true;
  progress.value = 0;
  error.value = '';

  try {
    if (isFinancialAgent.value) {
      progressLabel.value = 'Analizando documento...';
      if (props.useAsyncFinancialAnalysis) {
        progressLabel.value = 'Subiendo archivo...';
        const queued = await financialService.uploadAndAnalyzeAsync(
          pendingFile.value,
          {
            agentId: props.agentId,
            periodStart: toISODate(periodStart.value),
            periodEnd: toISODate(periodEnd.value),
            periodLabel: periodLabel.value,
            framework: selectedFramework.value,
            entityType: selectedEntityType.value,
            tenantId: isAdmin.value ? ownershipTenantId.value : undefined,
            subTenantId: isAdmin.value ? ownershipSubTenantId.value : undefined,
            timeoutMs: props.analysisTimeoutMs,
          },
          (p) => {
            // Etapa upload: 0% -> 35%
            progress.value = Math.max(0, Math.min(35, Math.round((Number(p || 0) / 100) * 35)));
          }
        );
        progress.value = Math.max(progress.value, 40);
        progressLabel.value = queued?.message || 'Análisis en cola...';
        emit('task-queued', {
          taskId: queued?.task_id,
          status: queued?.status || 'pending',
          progressPercent: Number(queued?.progress_percent ?? 0),
          message: queued?.message || 'Análisis en cola...',
          errorMessage: null,
          filename: queued?.filename || pendingFile.value?.name || null,
          result: null,
          analysisRunId: null,
        });
        const result = await pollFinancialTask(
          queued.task_id,
          isAdmin.value ? ownershipTenantId.value : undefined,
        );
        emit('analyzed', result);
      } else {
        const result = await financialService.uploadAndAnalyze(
          pendingFile.value,
          {
            agentId: props.agentId,
            periodStart: toISODate(periodStart.value),
            periodEnd: toISODate(periodEnd.value),
            periodLabel: periodLabel.value,
            framework: selectedFramework.value,
            entityType: selectedEntityType.value,
            tenantId: isAdmin.value ? ownershipTenantId.value : undefined,
            subTenantId: isAdmin.value ? ownershipSubTenantId.value : undefined,
            timeoutMs: props.analysisTimeoutMs,
          },
          (p) => { progress.value = p; }
        );
        emit('analyzed', result);
      }
    } else {
      progressLabel.value = 'Subiendo...';
      const result = await documentsService.upload(
        pendingFile.value,
        {
          agentId: props.agentId,
          description: description.value,
          periodStart: toISODate(periodStart.value),
          periodEnd: toISODate(periodEnd.value),
          periodLabel: periodLabel.value,
          tenantId: isAdmin.value ? ownershipTenantId.value : undefined,
          subTenantId: isAdmin.value ? ownershipSubTenantId.value : undefined,
        },
        (p) => { progress.value = p; }
      );
      emit('uploaded', result);
    }

    progress.value = 100;
    clearFile();
  } catch (e) {
    const isTimeout = e.code === 'ECONNABORTED' || String(e.message || '').toLowerCase().includes('timeout');
    const msg = isTimeout
      ? 'El análisis está tardando más de lo esperado. Intenta con un archivo más pequeño o vuelve a intentar en unos minutos.'
      : (e.response?.data?.detail || e.message || 'Error al subir el archivo');
    error.value = msg;
    emit('error', msg);
  } finally {
    uploading.value = false;
  }
};
</script>

<style scoped>
.doc-upload { width: 100%; }

.drop-zone {
  border: 2px dashed #d1d5db;
  border-radius: 10px;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: #fafafa;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.drop-zone:hover, .drop-zone.drag-over {
  border-color: #6366f1;
  background: #f0f0ff;
}
.drop-zone.has-file {
  border-style: solid;
  border-color: #6366f1;
  background: #f5f3ff;
}

.drop-content { display: flex; flex-direction: column; align-items: center; gap: 0.4rem; }
.drop-icon { font-size: 2.5rem; color: #9ca3af; }
.drop-title { font-weight: 600; color: #374151; margin: 0; }
.drop-hint { font-size: 0.8rem; color: #9ca3af; margin: 0; }

.file-preview {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0 0.5rem;
}
.file-icon { font-size: 2rem; color: #6366f1; flex-shrink: 0; }
.file-info { flex: 1; text-align: left; overflow: hidden; }
.file-name { display: block; font-weight: 600; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.file-size { font-size: 0.8rem; color: #6b7280; }

.progress-wrap { display: flex; flex-direction: column; gap: 0.25rem; }
.progress-label { font-size: 0.8rem; color: #6b7280; text-align: center; }

.w-full { width: 100%; }
.mt-2 { margin-top: 0.5rem; }
.field { }
</style>

<style scoped>
.doc-upload { width: 100%; display: flex; flex-direction: column; gap: 0.75rem; }

/* Selector de período */
.period-selector {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 10px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}
.period-header {
  display: flex; align-items: center; gap: 0.5rem;
  font-weight: 600; color: #0369a1; font-size: 0.9rem;
}
.required { color: #ef4444; }
.period-quick { display: flex; flex-wrap: wrap; gap: 0.35rem; }
.period-dates { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
.field label { display: block; font-size: 0.8rem; color: #374151; margin-bottom: 0.3rem; font-weight: 500; }
.period-label-preview {
  display: flex; align-items: center; gap: 0.4rem;
  font-size: 0.85rem; color: #0369a1; font-weight: 600;
  background: #e0f2fe; padding: 0.4rem 0.75rem; border-radius: 6px;
}

/* Selector de marco normativo */
.framework-selector {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}
.framework-header {
  display: flex; align-items: center; gap: 0.5rem;
  font-weight: 600; color: #15803d; font-size: 0.9rem;
}

/* Selector de tipo de entidad */
.entity-type-selector {
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.ownership-selector {
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  border-radius: 10px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.ownership-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #3730a3;
  font-size: 0.9rem;
}
.entity-type-header {
  display: flex; align-items: center; gap: 0.5rem;
  font-weight: 600; color: #92400e; font-size: 0.9rem;
}

/* Zona de drop */
.drop-zone {
  border: 2px dashed #d1d5db;
  border-radius: 10px;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: #fafafa;
  min-height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.drop-zone:hover, .drop-zone.drag-over { border-color: #6366f1; background: #f0f0ff; }
.drop-zone.has-file { border-style: solid; border-color: #6366f1; background: #f5f3ff; }

.drop-content { display: flex; flex-direction: column; align-items: center; gap: 0.4rem; }
.drop-icon { font-size: 2.5rem; color: #9ca3af; }
.drop-title { font-weight: 600; color: #374151; margin: 0; }
.drop-hint { font-size: 0.8rem; color: #9ca3af; margin: 0; }

.file-preview { display: flex; align-items: center; gap: 0.75rem; width: 100%; padding: 0 0.5rem; }
.file-icon { font-size: 2rem; color: #6366f1; flex-shrink: 0; }
.file-info { flex: 1; text-align: left; overflow: hidden; }
.file-name { display: block; font-weight: 600; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.file-size { font-size: 0.8rem; color: #6b7280; }

.progress-wrap { display: flex; flex-direction: column; gap: 0.25rem; }
.progress-label { font-size: 0.8rem; color: #6b7280; text-align: center; }

.w-full { width: 100%; }
.mt-1 { margin-top: 0.25rem; }
.mt-2 { margin-top: 0.5rem; }
</style>
